import type { SalesAnalysis, SalesGrain, SalesRow } from '@morales/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { BarList, Donut, Sparkline, TrendChart, compact } from '../components/charts';
import { PeriodBar, presets, usePeriod } from '../components/PeriodBar';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { byNumber, byText, useSort } from '../components/useSort';
import { Empty, QueryView } from '../components/ui';
import { formatMoney, formatQty } from '../format';
import { useSearchParam } from '../hooks';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';
import { Trend } from '../components/Signals';

const RANGES = presets('d30', 'd7', 'd90', 'd365');
const GRAINS: readonly SalesGrain[] = ['day', 'week', 'month'];
type SortKey = 'name' | 'revenue' | 'prior' | 'change' | 'pct' | 'share' | 'gp' | 'gm' | 'invoices' | 'qty';

/** A category keeps its color whatever the period shows (the seeded order: fruit, vegetable, chile, dry, herbs, supplies). */
export function categoryColor(sort: number): string {
  const slot = { 10: 1, 20: 2, 30: 3, 40: 4, 50: 5, 90: 6 }[sort];
  return slot ? `var(--viz-${slot})` : 'var(--viz-other)';
}

/** The bucket's name on the axis and its full name in the tooltip. */
export function bucketNames(start: string, days: number, grain: SalesGrain, intlLocale: string, t: (k: AppMessageKey) => string) {
  const d = new Date(`${start}T00:00:00Z`);
  const f = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(intlLocale, { ...o, timeZone: 'UTC' }).format(d);
  const full = grain === 'week' ? 7 : grain === 'day' ? 1 : new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  const partial = days < full;
  const axis = grain === 'month' ? f({ month: 'short' }) : f({ month: 'short', day: 'numeric' });
  const name = grain === 'month' ? f({ month: 'long', year: 'numeric' }) : grain === 'week' ? t('weekOf').replace('{date}', f({ month: 'short', day: 'numeric', year: 'numeric' })) : f({ weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  return { axis, title: partial ? `${name} (${t('nDaysInPeriod').replace('{n}', String(days))})` : name, partial };
}

/** Reports → Sales performance (decision 84): the period against the one before and a year ago, the trend, the mix, and every customer and item. */
export function SalesPage() {
  const { t, intlLocale } = useLocale();
  const navigate = useNavigate();
  const period = usePeriod(RANGES);
  const [grainRaw, setGrain] = useSearchParam('grain');
  const [tabRaw, setTab] = useSearchParam('by');
  const tab = tabRaw === 'item' ? 'item' : 'customer';
  const asked = GRAINS.includes(grainRaw as SalesGrain) ? (grainRaw as SalesGrain) : undefined;
  const query = useQuery({
    queryKey: qk.sales(period.from, period.to, asked ?? ''),
    queryFn: ({ signal }) => api.sales({ from: period.from, to: period.to, ...(asked ? { grain: asked } : {}) }, signal),
    placeholderData: keepPreviousData,
  });
  const [q, setQ] = useState('');
  const money = (v: number) => formatMoney(v, intlLocale);
  const sorters = useMemo(
    () => ({
      name: byText<SalesRow>((r) => r.name),
      revenue: byNumber<SalesRow>((r) => r.revenue),
      prior: byNumber<SalesRow>((r) => r.prior),
      change: byNumber<SalesRow>((r) => r.change),
      pct: byNumber<SalesRow>((r) => r.pct),
      share: byNumber<SalesRow>((r) => r.share),
      gp: byNumber<SalesRow>((r) => r.grossProfit),
      gm: byNumber<SalesRow>((r) => r.gmPct),
      invoices: byNumber<SalesRow>((r) => r.invoices),
      qty: byNumber<SalesRow>((r) => r.qty),
    }),
    [],
  );
  const { sorted, head } = useSort<SalesRow, SortKey>(sorters, { key: 'revenue', dir: -1 }, ['revenue', 'prior', 'change', 'pct', 'share', 'gp', 'gm', 'invoices', 'qty']);
  const data = query.data;
  const grain = data?.grain ?? asked ?? 'week';
  // the server groups a long period by week or month whatever is asked; those buttons are off
  const span = Math.round((Date.parse(`${period.to}T00:00:00Z`) - Date.parse(`${period.from}T00:00:00Z`)) / 86_400_000) + 1;

  return (
    <div className="desk-page">
      <header className="desk-head">
        <div>
          <h1>{t('salesTitle')}</h1>
          <p className="muted">{t('salesHint')}</p>
        </div>
      </header>
      <div className="toolbar">
        <PeriodBar presets={RANGES} period={period} />
        <div className="seg" role="group" aria-label={t('grainLabel')}>
          {GRAINS.map((g) => (
            <button key={g} type="button" aria-pressed={grain === g} disabled={(g === 'day' && span > 92) || (g === 'week' && span > 731)} onClick={() => setGrain(g)}>
              {t(`grain_${g}`)}
            </button>
          ))}
        </div>
      </div>
      <QueryView query={query}>
        {(s) => (
          <div className="stack" style={{ opacity: query.isPlaceholderData ? 0.6 : 1 }}>
            <Kpis s={s} />
            {s.kpis.revenue.now === 0 && s.kpis.revenue.prior === 0 && s.kpis.revenue.lastYear === 0 ? (
              <Empty>{t('noSalesInPeriod')}</Empty>
            ) : (
              <>
                <Charts s={s} />
                <nav className="subnav" aria-label={t('salesTitle')}>
                  {(['customer', 'item'] as const).map((k) => (
                    <a key={k} href={`#${k}`} className={tab === k ? 'active' : undefined} aria-current={tab === k ? 'page' : undefined} onClick={(e) => { e.preventDefault(); setTab(k === 'item' ? 'item' : ''); }}>
                      {k === 'item' ? t('salesByItem') : t('salesByCustomer')}
                    </a>
                  ))}
                  <input className="tool-search" type="search" placeholder={tab === 'item' ? t('findItem') : t('findCustomer')} aria-label={t('search')} value={q} onChange={(e) => setQ(e.target.value)} />
                </nav>
                <RowsSheet
                  s={s}
                  rows={sorted((tab === 'item' ? s.items : s.customers).filter((r) => !q.trim() || r.name.toLowerCase().includes(q.trim().toLowerCase())))}
                  by={tab}
                  head={head}
                  money={money}
                  onOpen={(r) => navigate(tab === 'item' ? `/items/${r.id}/intel` : `/customers/${r.id}`)}
                />
              </>
            )}
          </div>
        )}
      </QueryView>
    </div>
  );
}

function Kpis({ s }: { s: SalesAnalysis }) {
  const { t, intlLocale } = useLocale();
  const money = (v: number) => formatMoney(v, intlLocale);
  const k = s.kpis;
  const gmDelta = (a: number | null, b: number | null) => (a === null || b === null ? null : Math.round((a - b) * 10) / 10);
  const tiles: { key: string; label: string; value: string; pct: number | null; lyPct: number | null; unit?: string; hint?: string }[] = [
    { key: 'rev', label: t('revenue'), value: money(k.revenue.now), pct: k.revenue.pct, lyPct: k.revenue.lyPct },
    ...(k.grossProfit ? [{ key: 'gp', label: t('grossProfit'), value: money(k.grossProfit.now), pct: k.grossProfit.pct, lyPct: k.grossProfit.lyPct }] : []),
    ...(k.gmPct
      ? [{
          key: 'gm', label: t('gmCol'), value: k.gmPct.now === null ? '—' : `${k.gmPct.now}%`, pct: gmDelta(k.gmPct.now, k.gmPct.prior), lyPct: gmDelta(k.gmPct.now, k.gmPct.lastYear), unit: ` ${t('pointsUnit')}`,
          ...(k.coveredPct < 100 ? { hint: `${t('costKnownFor')} ${k.coveredPct}%` } : {}),
        }]
      : []),
    { key: 'inv', label: t('kpiInvoices'), value: String(k.invoices.now), pct: k.invoices.pct, lyPct: k.invoices.lyPct },
    { key: 'avg', label: t('avgInvoice'), value: money(k.avgInvoice.now), pct: k.avgInvoice.pct, lyPct: k.avgInvoice.lyPct },
    { key: 'cust', label: t('activeCustomers'), value: String(k.customers.now), pct: k.customers.pct, lyPct: k.customers.lyPct },
  ];
  return (
    <div className="kpi-row" data-testid="sales-kpis">
      {tiles.map((x) => (
        <div key={x.key} className="kpi" title={x.hint}>
          <span className="kpi-label">{x.label}</span>
          <span className="kpi-value">{x.value}</span>
          <span className="kpi-delta">{t('vsPrior')} <Trend pct={x.pct} {...(x.unit ? { unit: x.unit } : {})} /></span>
          <span className="kpi-delta">{t('vsYearAgo')} <Trend pct={x.lyPct} {...(x.unit ? { unit: x.unit } : {})} /></span>
        </div>
      ))}
    </div>
  );
}

function Charts({ s }: { s: SalesAnalysis }) {
  const { t, intlLocale } = useLocale();
  const money = (v: number) => formatMoney(v, intlLocale);
  const points = s.series.map((b) => {
    const nm = bucketNames(b.start, b.days, s.grain, intlLocale, t);
    return { key: b.start, axis: nm.axis, title: nm.title, faded: nm.partial };
  });
  const per = t(`per_${s.grain}`);
  const cats = s.categories.map((c) => ({ key: c.name || '-', label: c.name || t('uncategorized'), value: c.revenue, color: categoryColor(c.sort) }));
  return (
    <div className="chart-grid">
      <section className="wide" data-testid="sales-trend">
        <div className="chart-head">
          <h2>{t('revenueBy').replace('{per}', per)}</h2>
        </div>
        <TrendChart
          label={t('revenueBy').replace('{per}', per)}
          points={points}
          bar={{ name: t('thisPeriod'), values: s.series.map((b) => b.revenue), color: 'var(--viz-1)' }}
          line={{ name: t('lastYear'), values: s.series.map((b) => b.lastYear), color: 'var(--viz-2)' }}
          format={money}
          axisFormat={(v) => compact(v, intlLocale)}
          fadedNote={t('partialNote')}
          height={240}
        />
      </section>
      {s.showCost ? (
        <section>
          <h2>{t('gmBy').replace('{per}', per)}</h2>
          <TrendChart
            label={t('gmBy').replace('{per}', per)}
            points={points}
            line={{ name: t('gmCol'), values: s.series.map((b) => b.gmPct), color: 'var(--viz-1)' }}
            format={(v) => `${v}%`}
            axisFormat={(v) => `${v}%`}
            height={200}
          />
        </section>
      ) : null}
      <section data-testid="sales-mix">
        <h2>{t('salesMix')}</h2>
        <Donut label={t('salesMix')} slices={cats} format={(v) => compact(v, intlLocale)} centerLabel={t('revenue')} otherLabel={t('otherCategories')} />
      </section>
      <section>
        <h2>{t('topCustomers')}</h2>
        <BarList
          label={t('topCustomers')}
          rows={s.customers.filter((c) => c.revenue > 0).slice(0, 10).map((c) => ({ key: c.id, label: c.name, value: c.revenue, display: compact(c.revenue, intlLocale), href: `/customers/${c.id}`, sub: <Trend pct={c.pct} /> }))}
        />
      </section>
      <section>
        <h2>{t('topItems')}</h2>
        <BarList
          label={t('topItems')}
          rows={s.items.filter((c) => c.revenue > 0).slice(0, 10).map((c) => ({ key: c.id, label: c.name, value: c.revenue, display: compact(c.revenue, intlLocale), href: `/items/${c.id}/intel`, sub: <Trend pct={c.pct} /> }))}
        />
      </section>
    </div>
  );
}

function RowsSheet({ s, rows, by, head, money, onOpen }: { s: SalesAnalysis; rows: SalesRow[]; by: 'customer' | 'item'; head: (k: SortKey, label: string) => React.ReactNode; money: (v: number) => string; onOpen: (r: SalesRow) => void }) {
  const { t } = useLocale();
  const cols: SheetColumn<SalesRow>[] = [
    { key: 'name', header: head('name', by === 'item' ? t('itemCol') : t('customer')), label: by === 'item' ? t('itemCol') : t('customer'), width: '240px', render: (r) => r.name },
    { key: 'trend', header: t('trendCol'), width: '112px', render: (r) => <Sparkline values={r.trend} label={`${r.name} ${t('trendCol')}`} /> },
    { key: 'revenue', header: head('revenue', t('revenue')), label: t('revenue'), width: '110px', align: 'right', render: (r) => money(r.revenue) },
    { key: 'prior', header: head('prior', t('priorPeriod')), label: t('priorPeriod'), width: '110px', align: 'right', render: (r) => money(r.prior) },
    { key: 'change', header: head('change', t('changeCol')), label: t('changeCol'), width: '110px', align: 'right', render: (r) => money(r.change), cellClass: (r) => (r.change < 0 ? 'bad' : undefined) },
    { key: 'pct', header: head('pct', t('changePctCol')), label: t('changePctCol'), width: '90px', align: 'right', render: (r) => <Trend pct={r.pct} /> },
    { key: 'share', header: head('share', t('shareCol')), label: t('shareCol'), width: '80px', align: 'right', render: (r) => `${r.share}%` },
    ...(s.showCost
      ? [
          { key: 'gp', header: head('gp', t('grossProfit')), label: t('grossProfit'), width: '110px', align: 'right', render: (r) => (r.grossProfit === null ? '' : money(r.grossProfit)) } as SheetColumn<SalesRow>,
          { key: 'gm', header: head('gm', t('gmCol')), label: t('gmCol'), width: '80px', align: 'right', render: (r) => (r.gmPct === null ? '' : `${r.gmPct}%`) } as SheetColumn<SalesRow>,
        ]
      : []),
    by === 'item'
      ? { key: 'qty', header: head('qty', t('qtySold')), label: t('qtySold'), width: '80px', align: 'right', render: (r) => formatQty(r.qty) }
      : { key: 'invoices', header: head('invoices', t('invoicesCount')), label: t('invoicesCount'), width: '80px', align: 'right', render: (r) => r.invoices },
  ];
  const total = rows.reduce((a, r) => ({ revenue: a.revenue + r.revenue, prior: a.prior + r.prior }), { revenue: 0, prior: 0 });
  const footer = (
    <tr>
      <td colSpan={cols.length} className="muted">
      {t('nRows').replace('{n}', String(rows.length))} · {t('revenue')} {money(Math.round(total.revenue * 100) / 100)} · {t('priorPeriod')} {money(Math.round(total.prior * 100) / 100)}
      </td>
    </tr>
  );
  return rows.length === 0 ? (
    <Empty>{t('noResults')}</Empty>
  ) : (
    <Sheet label={by === 'item' ? t('salesByItem') : t('salesByCustomer')} columns={cols} rows={rows} rowKey={(r) => r.id} rowLabel={(r) => r.name} stickyFirst minWidth="1100px" footer={footer} onOpen={onOpen} />
  );
}
